import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  CheckCircle, 
  X, 
  ArrowRight,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import UpgradeModal, { TRIGGER_SOURCES } from "@/components/UpgradeModal";

export default function BehaviorAlerts({ 
  className = "", 
  showOnlyOne = true, 
  onOpenUnpaidModal,
  onOpenOverdueModal,
  survivalMode = false,
  unifiedStatus = null,
  startingBalance = null,
  "data-guide": dataGuide
}) {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch alerts when startingBalance changes
  useEffect(() => {
    fetchAlerts();
  }, [startingBalance]);

  const fetchAlerts = async () => {
    try {
      const balance = startingBalance ?? user?.starting_balance ?? 0;
      const response = await axios.get(
        `${API}/guidance/behavior-alerts?starting_balance=${balance}`, 
        { withCredentials: true }
      );
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      await axios.post(`${API}/guidance/dismiss-alert`, 
        { alert_id: alertId },
        { withCredentials: true }
      );
      setDismissed(prev => new Set([...prev, alertId]));
    } catch (error) {
      console.error("Error dismissing alert:", error);
    }
  };

  const handleAction = (route) => {
    navigate(route);
  };

  const handleFixShortfall = (alert) => {
    // If user is premium, navigate to bills
    if (user?.is_premium) {
      navigate("/bills");
    } else {
      // Show upgrade modal with shortfall context
      setUpgradeContext({
        shortfall_amount: alert.message.match(/£([\d,.]+)/)?.[1] || "unknown"
      });
      setUpgradeModalOpen(true);
    }
  };

  // Handle "Mark as paid" click - opens UnpaidBillsModal
  const handleMarkAsPaid = () => {
    if (onOpenUnpaidModal) {
      onOpenUnpaidModal();
    } else {
      navigate("/bills");
    }
  };

  // Handle "Review overdue bills" click - opens OverdueConfirmationModal
  const handleReviewOverdue = () => {
    if (onOpenOverdueModal) {
      onOpenOverdueModal();
    } else {
      navigate("/bills");
    }
  };

  // Check if alert has "Mark as paid" action
  const isMarkAsPaidAlert = (alert) => {
    return alert.id === "missed_bills" || alert.id === "unpaid_bills";
  };

  // Check if alert is overdue unconfirmed alert
  const isOverdueUnconfirmedAlert = (alert) => {
    return alert.id === "overdue_unconfirmed";
  };

  // Filter alerts
  let visibleAlerts = alerts.filter(a => !dismissed.has(a.id));
  
  // In survival mode, only show critical/urgent alerts
  if (survivalMode) {
    visibleAlerts = visibleAlerts.filter(a => 
      a.type === 'critical' || 
      a.type === 'warning' ||
      isMarkAsPaidAlert(a)
    );
  }
  
  // Filter out contradictory alerts based on unified status
  // If SafeToSpend says we're safe, don't show "tight" warnings
  if (unifiedStatus && unifiedStatus.status === 'safe') {
    visibleAlerts = visibleAlerts.filter(a => {
      // Don't filter out critical alerts
      if (a.type === 'critical') return true;
      // Filter out "tight" or "short" warnings when status is safe
      const message = (a.message || '').toLowerCase();
      if (message.includes('tight') || message.includes('may be short')) {
        return false;
      }
      return true;
    });
  }
  
  // Priority order: critical > warning > info > success
  const priorityOrder = { critical: 0, warning: 1, info: 2, success: 3 };
  const sortedAlerts = [...visibleAlerts].sort((a, b) => 
    (priorityOrder[a.type] || 4) - (priorityOrder[b.type] || 4)
  );
  
  // Show only the highest priority alert if showOnlyOne is true
  const displayAlerts = showOnlyOne ? sortedAlerts.slice(0, 1) : sortedAlerts;

  if (loading || displayAlerts.length === 0) {
    return null;
  }

  const getAlertStyle = (type) => {
    switch (type) {
      case "critical":
        return {
          bg: "bg-red-500/10",
          border: "border-red-500/30",
          icon: AlertTriangle,
          iconColor: "text-red-400",
          titleColor: "text-red-300"
        };
      case "warning":
        return {
          bg: "bg-amber-500/10",
          border: "border-amber-500/30",
          icon: AlertCircle,
          iconColor: "text-amber-400",
          titleColor: "text-amber-300"
        };
      case "success":
        return {
          bg: "bg-emerald-500/10",
          border: "border-emerald-500/30",
          icon: CheckCircle,
          iconColor: "text-emerald-400",
          titleColor: "text-emerald-300"
        };
      default:
        return {
          bg: "bg-blue-500/10",
          border: "border-blue-500/30",
          icon: Info,
          iconColor: "text-blue-400",
          titleColor: "text-blue-300"
        };
    }
  };

  // Check if alert is a shortfall alert
  const isShortfallAlert = (alert) => {
    return alert.id === "low_balance" || alert.id === "tight_balance";
  };

  return (
    <>
      <div 
        className={`space-y-3 ${className}`} 
        data-testid="behavior-alerts"
      >
        {displayAlerts.map((alert, index) => {
          const style = getAlertStyle(alert.type);
          const Icon = style.icon;
          const showFixButton = isShortfallAlert(alert);
          
          // Place data-guide="alerts" ONLY on the first visible alert card (not wrapper)
          // This ensures the guide targets an element with actual dimensions
          const isFirstAlert = index === 0;

          return (
            <div 
              key={alert.id}
              className={`${style.bg} ${style.border} border rounded-xl p-4 animate-in slide-in-from-top-2 duration-300`}
              data-testid={`alert-${alert.id}`}
              data-guide={isFirstAlert && dataGuide ? dataGuide : undefined}
            >
              <div className="flex items-start gap-3">
                <Icon className={`w-5 h-5 mt-0.5 ${style.iconColor}`} />
                
                <div className="flex-1 min-w-0">
                  <h4 className={`font-semibold ${style.titleColor}`}>
                    {alert.title}
                  </h4>
                  <p className="text-sm text-slate-300 mt-1">
                    {alert.message}
                  </p>

                  {/* Show items if present (e.g., missed bills) */}
                  {alert.items && alert.items.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {alert.items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-slate-400">{item.name}</span>
                          <span className="text-slate-300">£{item.amount.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex items-center gap-3 mt-3">
                    {/* Fix this button for shortfall alerts */}
                    {showFixButton && (
                      <Button
                        size="sm"
                        className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm h-8 px-4"
                        onClick={() => handleFixShortfall(alert)}
                        data-testid="fix-shortfall-btn"
                      >
                        <Zap className="w-3.5 h-3.5 mr-1.5" />
                        Fix this
                        <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    )}
                    
                    {/* Mark as paid button for missed/unpaid bills alerts */}
                    {isMarkAsPaidAlert(alert) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-0 h-auto text-sm text-emerald-400 hover:text-emerald-300 hover:bg-transparent"
                        onClick={handleMarkAsPaid}
                        data-testid="mark-as-paid-btn"
                      >
                        Mark as paid
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                    
                    {/* Review overdue bills button */}
                    {isOverdueUnconfirmedAlert(alert) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-0 h-auto text-sm text-amber-400 hover:text-amber-300 hover:bg-transparent"
                        onClick={handleReviewOverdue}
                        data-testid="review-overdue-btn"
                      >
                        Review bills
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                    
                    {/* Standard action button */}
                    {alert.action && !showFixButton && !isMarkAsPaidAlert(alert) && !isOverdueUnconfirmedAlert(alert) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-0 h-auto text-sm text-emerald-400 hover:text-emerald-300 hover:bg-transparent"
                        onClick={() => handleAction(alert.action.route)}
                      >
                        {alert.action.text}
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Dismiss button */}
                {alert.dismissible && (
                  <button
                    onClick={() => handleDismiss(alert.id)}
                    className="text-slate-500 hover:text-slate-300 transition-colors"
                    aria-label="Dismiss alert"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Upgrade Modal for shortfall fix */}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        triggerSource={TRIGGER_SOURCES.SHORTFALL}
        contextData={upgradeContext}
      />
    </>
  );
}
